// Parse Maester / Pester JSON output into a structured shape that maps
// cleanly onto client_control_test_scans + client_control_test_results.
//
// Maester wraps Pester 5.x output. Multiple wrapper shapes exist depending
// on Maester version and how it was invoked. The parser is tolerant — it
// looks for `Tests` (or `Containers[].Blocks[]...Tests`) at any depth and
// pulls the per-test fields we care about, falling back gracefully when
// fields are absent.
//
// Returned shape:
//   {
//     scan: {
//       tenant_id, tenant_domain, tool_version, scanned_at,
//       total_tests, passed_count, failed_count, skipped_count, not_run_count
//     },
//     results: [
//       { test_id, test_name, description, category, severity, result,
//         result_message, remediation, reference_url, framework_tags, tags, metadata }
//     ],
//     parseWarnings: [string]   // surfaced to advisor in the UI
//   }

const FRAMEWORK_TAG_PATTERNS = [
  { pattern: /^cis(\.|$|-)/i, framework: 'CIS Controls' },
  { pattern: /^nist[-_]?csf/i, framework: 'NIST CSF' },
  { pattern: /^nist[-_]?800[-_]?53/i, framework: 'NIST SP 800-53' },
  { pattern: /^nist[-_]?800[-_]?171/i, framework: 'NIST SP 800-171' },
  { pattern: /^cmmc/i, framework: 'CMMC' },
  { pattern: /^iso[-_]?27001/i, framework: 'ISO 27001' },
  { pattern: /^soc[-_]?2/i, framework: 'SOC 2' },
  { pattern: /^pci/i, framework: 'PCI DSS' },
  { pattern: /^hipaa/i, framework: 'HIPAA' },
  { pattern: /^gdpr/i, framework: 'GDPR' },
  { pattern: /^eidsca/i, framework: 'EIDSCA' },
  { pattern: /^msec/i, framework: 'Microsoft Secure Score' },
  { pattern: /^cisa/i, framework: 'CISA' }
]

export function parseMaesterJson(input) {
  const parseWarnings = []
  let payload = input

  // Accept either string or object input
  if (typeof input === 'string') {
    try { payload = JSON.parse(input) }
    catch (err) {
      throw new Error(`Invalid JSON: ${err.message}`)
    }
  }
  if (!payload || typeof payload !== 'object') {
    throw new Error('Empty or non-object payload')
  }

  // Walk the payload to collect every Test record. Pester JSON nests Tests
  // under Containers > Blocks (recursively) > Tests, but Maester sometimes
  // flattens this into a top-level Tests array. We handle both.
  const tests = []
  collectTests(payload, tests)
  if (tests.length === 0) {
    parseWarnings.push('No test records found in the uploaded payload. The file may be from a different tool or in an unexpected format.')
  }

  // Aggregate metadata
  const scannedAt = pickFirst(payload, ['ExecutedAt', 'StartTime', 'StartedAt', 'Time', 'scannedAt']) || new Date().toISOString()
  const tenantId = pickFirst(payload, ['TenantId', 'tenantId', 'TenantID']) || extractTenantFromTests(tests)
  const tenantDomain = pickFirst(payload, ['TenantDomain', 'tenantDomain', 'Domain'])
  const toolVersion = pickFirst(payload, ['MaesterVersion', 'maesterVersion', 'Version', 'ToolVersion'])

  // Map each Pester test → result row
  const results = tests.map(t => mapTest(t)).filter(Boolean)

  // Totals
  const totals = {
    total_tests: results.length,
    passed_count: results.filter(r => r.result === 'passed').length,
    failed_count: results.filter(r => r.result === 'failed').length,
    skipped_count: results.filter(r => r.result === 'skipped').length,
    not_run_count: results.filter(r => r.result === 'not_run').length
  }

  return {
    scan: {
      tenant_id: tenantId || null,
      tenant_domain: tenantDomain || null,
      tool_version: toolVersion || null,
      scanned_at: normalizeDate(scannedAt) || new Date().toISOString(),
      ...totals
    },
    results,
    parseWarnings
  }
}

// ─── Internals ───

function collectTests(node, out) {
  if (!node || typeof node !== 'object') return
  // Direct Tests array
  if (Array.isArray(node.Tests)) {
    for (const t of node.Tests) {
      if (looksLikeTest(t)) out.push(t)
      else collectTests(t, out)
    }
  }
  if (Array.isArray(node.tests)) {
    for (const t of node.tests) {
      if (looksLikeTest(t)) out.push(t)
      else collectTests(t, out)
    }
  }
  // Recurse into common Pester nesting points
  for (const key of ['Containers', 'Blocks', 'Children', 'containers', 'blocks', 'children']) {
    if (Array.isArray(node[key])) {
      for (const child of node[key]) collectTests(child, out)
    }
  }
  // Maester sometimes wraps results under top-level keys
  for (const key of ['Result', 'results', 'TestResults', 'testResults']) {
    if (node[key] && typeof node[key] === 'object') collectTests(node[key], out)
  }
}

function looksLikeTest(t) {
  if (!t || typeof t !== 'object') return false
  // A Pester test typically has Name + Result (or Passed)
  const hasName = typeof t.Name === 'string' || typeof t.name === 'string' || typeof t.Title === 'string'
  const hasResult = t.Result !== undefined || t.result !== undefined || t.Passed !== undefined || t.passed !== undefined
  return hasName && hasResult
}

function mapTest(t) {
  const name = pickFirst(t, ['Name', 'name', 'Title', 'title']) || 'Untitled test'
  const rawResult = pickFirst(t, ['Result', 'result', 'Status', 'status'])
  const passedFlag = pickFirst(t, ['Passed', 'passed'])
  const result = normalizeResult(rawResult, passedFlag)

  const tags = collectTags(t)
  const frameworkTags = inferFrameworkTags(tags, name, pickFirst(t, ['Block', 'block']))

  // Maester test IDs often appear as "t.MT.1004" or similar in Name or Path
  const testId = pickFirst(t, ['Id', 'id', 'TestId', 'testId']) || extractTestIdFromName(name)

  // Description / message / remediation
  const description = truncate(pickFirst(t, ['Description', 'description', 'Synopsis', 'synopsis']), 4000)
  const errorRecord = t.ErrorRecord || t.errorRecord
  const message = truncate(
    formatMessage(errorRecord) || pickFirst(t, ['StandardOutput', 'standardOutput', 'Message', 'message', 'FailureMessage']),
    2000
  )
  const remediation = truncate(pickFirst(t, ['Remediation', 'remediation', 'Fix', 'HowToFix', 'howToFix']), 4000)
  const referenceUrl = truncate(pickFirst(t, ['HelpUrl', 'helpUrl', 'Url', 'url', 'Reference', 'reference', 'DocsUrl']), 1000)

  const severity = normalizeSeverity(pickFirst(t, ['Severity', 'severity', 'Importance', 'importance']))
  const category = truncate(pickFirst(t, ['Block', 'block', 'Category', 'category', 'Group', 'group']), 200)

  return {
    test_id: testId ? String(testId).slice(0, 200) : null,
    test_name: truncate(name, 500),
    description: description || null,
    category: category || null,
    severity: severity || null,
    result,
    result_message: message || null,
    remediation: remediation || null,
    reference_url: referenceUrl || null,
    framework_tags: frameworkTags,
    tags: tags.slice(0, 50).map(tag => String(tag).slice(0, 100)),
    metadata: buildCompactMetadata(t)
  }
}

function normalizeResult(rawResult, passedFlag) {
  if (passedFlag === true) return 'passed'
  if (passedFlag === false) return 'failed'
  if (!rawResult) return 'not_run'
  const r = String(rawResult).toLowerCase()
  if (r === 'passed' || r === 'pass' || r === 'success') return 'passed'
  if (r === 'failed' || r === 'fail' || r === 'error') return 'failed'
  if (r === 'skipped' || r === 'skip') return 'skipped'
  if (r === 'notrun' || r === 'not_run' || r === 'pending') return 'not_run'
  if (r === 'inconclusive') return 'inconclusive'
  return 'not_run'
}

function normalizeSeverity(s) {
  if (!s) return null
  const v = String(s).toLowerCase()
  if (['critical', 'high', 'medium', 'low', 'info'].includes(v)) return v
  // Pester "1/2/3" or numeric severity
  if (v === '1' || v === 'severe') return 'critical'
  if (v === '2') return 'high'
  if (v === '3') return 'medium'
  if (v === '4' || v === 'minor') return 'low'
  return null
}

function collectTags(t) {
  const out = new Set()
  const sources = [t.Tag, t.tag, t.Tags, t.tags]
  for (const src of sources) {
    if (!src) continue
    if (Array.isArray(src)) src.forEach(v => v && out.add(String(v)))
    else if (typeof src === 'string') src.split(/[,\s]+/).forEach(v => v && out.add(v))
  }
  return Array.from(out)
}

function inferFrameworkTags(tags, name, block) {
  const haystack = [...tags, name || '', block || ''].join(' ')
  const found = new Set()
  for (const { pattern, framework } of FRAMEWORK_TAG_PATTERNS) {
    if (pattern.test(haystack)) found.add(framework)
    // Also check each tag individually
    for (const tag of tags) {
      if (pattern.test(tag)) found.add(framework)
    }
  }
  return Array.from(found)
}

function extractTestIdFromName(name) {
  if (!name) return null
  // Patterns like "MT.1004 — MFA enforced..." or "[CIS-1.1] ..." or "EIDSCA.AS.01"
  const match = name.match(/\b([A-Z]{2,8}[-.][\w.-]+)/)
  return match ? match[1] : null
}

function extractTenantFromTests(tests) {
  for (const t of tests) {
    const tid = t.TenantId || t.tenantId
    if (tid) return tid
  }
  return null
}

function formatMessage(errorRecord) {
  if (!errorRecord) return null
  if (typeof errorRecord === 'string') return errorRecord
  if (Array.isArray(errorRecord)) {
    return errorRecord
      .map(e => typeof e === 'string' ? e : (e?.Exception?.Message || e?.Message || JSON.stringify(e)))
      .filter(Boolean)
      .join('\n')
  }
  return errorRecord?.Exception?.Message || errorRecord?.Message || null
}

function buildCompactMetadata(t) {
  // Allowlist a small set of identifying fields. Everything else stays in the
  // parent scan's raw_payload — duplicating Pester's verbose per-test fields
  // here was blowing past PostgREST's request body limit on large scans.
  const out = {}
  const keep = ['Id', 'id', 'TestId', 'testId', 'ExpandedName', 'expandedName', 'Path', 'path']
  for (const k of keep) {
    if (t[k] !== undefined && t[k] !== null) {
      const v = t[k]
      if (typeof v === 'string') out[k] = v.slice(0, 500)
      else if (typeof v === 'number' || typeof v === 'boolean') out[k] = v
      else if (Array.isArray(v)) out[k] = v.slice(0, 20).map(x => typeof x === 'string' ? x.slice(0, 200) : x)
    }
  }
  return out
}

function truncate(value, max) {
  if (value === null || value === undefined) return null
  const s = typeof value === 'string' ? value : String(value)
  return s.length > max ? s.slice(0, max) + '…' : s
}

function pickFirst(obj, keys) {
  for (const k of keys) {
    if (obj && obj[k] !== undefined && obj[k] !== null && obj[k] !== '') return obj[k]
  }
  return null
}

function normalizeDate(value) {
  if (!value) return null
  try {
    const d = new Date(value)
    return isNaN(d.getTime()) ? null : d.toISOString()
  } catch { return null }
}
